import { requestSearchSuggest } from '../../bind/search';
import { RuntimeClientAtom } from '@vs-shop/engine/runtime/RuntimeClient';
import { tagged } from '@vs-shop/engine/data/util/tagged';
import * as Atom from 'effect/unstable/reactivity/Atom';

/**
 * Client-runtime atom family for live search suggestions.
 *
 * Keyed by a `tagged` value so the family memoizes on structural equality of
 * `{ q }`. Each member runs `requestSearchSuggest` on the client `ManagedRuntime`
 * and exposes an `AsyncResult` (initial / waiting / success / failure).
 *
 * `Atom.keepAlive` is **on**: a member's `AsyncResult` survives after the popover
 * closes (its consumer unmounts), so re-opening with the **same** `q` replays the
 * cached result instead of refetching. A new request only fires when `q` actually
 * changes. Tradeoff: each distinct query typed in a session stays resident for the
 * page's lifetime (bounded by distinct queries, not keystrokes — the debounce
 * collapses `a`/`au`/`aut` into far fewer committed keys). Revisit with a bounded
 * cache/TTL if the resident set ever grows uncomfortably.
 */

type SearchSuggestKey = {
  readonly _tag: 'SearchSuggestKey';
  readonly q: string;
};

const SearchSuggestKey = tagged<SearchSuggestKey>('SearchSuggestKey');

const searchSuggestAtomFamily = Atom.family((key: SearchSuggestKey) =>
  RuntimeClientAtom.atom(requestSearchSuggest({ q: key.q })).pipe(Atom.keepAlive)
);

export const getSearchSuggestAtom = (q: string) => searchSuggestAtomFamily(SearchSuggestKey({ q }));
